import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useAuth } from './auth-context.js';

interface PreferenceOptions {
  /** URL filters (for example dashboard tiles) win over the saved preference. */
  urlKeys?: string[];
  validate?: (value: unknown) => boolean;
}

function readPreference<T>(storageKey: string, fallback: T, validate?: (value: unknown) => boolean): T {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    const value = JSON.parse(raw) as unknown;
    const valid = validate
      ? validate(value)
      : fallback === null || typeof fallback === 'object' || typeof value === typeof fallback;
    return valid ? value as T : fallback;
  } catch {
    return fallback;
  }
}

/** Browser-local UI preference, isolated by OPS user and module/key. */
export function useUserPreference<T>(key: string, initialValue: T, options: PreferenceOptions = {}): [T, Dispatch<SetStateAction<T>>] {
  const { user } = useAuth();
  const storageKey = `ops.ui.v1.${user?.id ?? 'signed-out'}.${key}`;
  const initialRef = useRef(initialValue);
  initialRef.current = initialValue;
  const validateRef = useRef(options.validate);
  validateRef.current = options.validate;
  const hasUrlOverride = useRef(options.urlKeys?.some((name) => new URLSearchParams(window.location.search).has(name)) ?? false).current;
  const load = useCallback(
    () => hasUrlOverride ? initialRef.current : readPreference(storageKey, initialRef.current, validateRef.current),
    [hasUrlOverride, storageKey],
  );
  const [value, setValue] = useState<T>(load);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const next = load();
    valueRef.current = next;
    setValue(next);
  }, [load]);

  const setPreference = useCallback<Dispatch<SetStateAction<T>>>((action) => {
    const next = typeof action === 'function'
      ? (action as (current: T) => T)(valueRef.current)
      : action;
    valueRef.current = next;
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* private mode / storage full */ }
    setValue(next);
  }, [storageKey]);

  return [value, setPreference];
}

export const isStringPreference = (value: unknown) => typeof value === 'string';
export const isBooleanPreference = (value: unknown) => typeof value === 'boolean';
